import { Eye, GitBranch, Layers } from "lucide-react";
import type { ReactNode } from "react";

type PainCard = {
  icon: ReactNode;
  number: string;
  headline: string;
  body: string;
  resolution: string;
};

const PAIN_CARDS: PainCard[] = [
  {
    icon: <Eye className="h-5 w-5 text-primary" />,
    number: "01",
    headline: "It breaks and you can't see why.",
    body: "No observability. Routing failures are invisible. You can't replay what the agent did or why it took a wrong turn. Every production incident is a black box.",
    resolution:
      "Anter gives every run a durable audit trail, streaming replay from any checkpoint, and OpenTelemetry tracing across every tool call and handoff.",
  },
  {
    icon: <GitBranch className="h-5 w-5 text-primary" />,
    number: "02",
    headline: "Your routing logic doesn't scale.",
    body: "Single-agent systems hit a ceiling fast. Multi-step workflows need real transfer and delegation semantics — not chained prompts you hope hold together.",
    resolution:
      "Anter's orchestration engine handles transfer, delegation, and fallback with routing graphs validated at both write-time and runtime. Every decision is audited.",
  },
  {
    icon: <Layers className="h-5 w-5 text-primary" />,
    number: "03",
    headline: "You're building infra instead of product.",
    body: "Redis session management, SSE streaming, multi-LLM routing, approval gates, tenant isolation — these aren't differentiated features. They're table stakes that eat your roadmap.",
    resolution:
      "Anter ships all of it pre-built and production-tested, so your team stays focused on the AI behavior that actually matters to your users.",
  },
];

export default function WhyAnter() {
  return (
    <section className="py-20">
      <div className="mb-12">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-text">
          Why Anter
        </p>
        <h2 className="max-w-2xl text-3xl font-extrabold tracking-tight text-text sm:text-4xl">
          Most teams start with a prototype.
          <br />
          Then production happens.
        </h2>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-text-muted">
          Building an AI agent takes a weekend. Shipping one that works reliably, scales with your
          users, and doesn't page you at 3am — that's the hard part.
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {PAIN_CARDS.map((card) => (
          <div key={card.number} className="glass-panel group rounded-2xl p-6">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-primary-border bg-primary-dim">
                {card.icon}
              </div>
              <span className="font-mono text-xs font-bold text-text-faint">{card.number}</span>
            </div>
            <h3 className="mb-2 text-base font-bold text-text">{card.headline}</h3>
            <p className="mb-4 text-sm leading-relaxed text-text-muted">{card.body}</p>
            <div className="rounded-lg border border-primary-border/40 bg-primary-dim/50 p-3">
              <p className="text-xs leading-relaxed text-primary-text">{card.resolution}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
